import { useQuery } from '@tanstack/react-query';

// Via our own /domain-icon proxy: loading apple-touch-icon.png straight off the
// third-party domain gets blocked by Cross-Origin-Resource-Policy on many sites.
// The proxy rejects localhost, IP literals and single-label names, so skip those
// up front (e.g. native OAuth clients redirecting to http://localhost:PORT).
const isPublicHostname = (host: string): boolean => {
    const lower = host.toLowerCase();
    if (lower === 'localhost' || lower.endsWith('.localhost')) return false;
    // Dotted name whose last label (the TLD) contains a letter: rules out IPv4
    // literals, while bracketed IPv6 literals fail the character class.
    return /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*\.[a-z0-9-]*[a-z][a-z0-9-]*$/.test(lower);
};

const appleTouchIconFor = (favSourceUrl?: string | null): string | null => {
    if (!favSourceUrl) return null;
    try {
        const url = new URL(favSourceUrl);
        if (!isPublicHostname(url.hostname)) return null;
        return `/domain-icon?domain=${encodeURIComponent(url.hostname)}`;
    }
    catch {
        return null;
    }
};

// Resolves when the image actually loads; rejects for 404s/non-images. An Image probe
// rather than fetch() because cross-origin responses without CORS headers hide their
// status from fetch, while image decoding works regardless.
const probeImage = (url: string) =>
    new Promise<string>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(url);
        img.onerror = () => reject(new Error(`Image not available: ${url}`));
        img.src = url;
    });

// Returns iconUrl when provided; otherwise falls back to the apple-touch-icon of
// favSourceUrl's domain — checked for liveness, so callers never render a dead image.
// Returns null while resolving or when neither is available.
export const useAppleTouchIconFallback = (iconUrl?: string | null, favSourceUrl?: string | null) => {

    const fallback = appleTouchIconFor(favSourceUrl);

    const { data: icon } = useQuery({
        queryKey: ['apple-touch-icon', iconUrl ?? null, fallback],
        enabled: !!(iconUrl || fallback),
        retry: false,
        staleTime: Infinity,
        queryFn: async (): Promise<string | null> => {
            if (iconUrl) return iconUrl;
            try {
                return await probeImage(fallback!);
            }
            catch {
                return null;
            }
        },
    });

    return icon ?? null;
};
